import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { useEffect, useState, useRef } from 'react'
import { Plus, Upload, Trash2, Download, Pencil } from 'lucide-react'
import { fmtDate, fmtEuro, hoje } from '../utils/fmt'

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace('/api', '')

// ─── RECEITAS ────────────────────────────────────────────────────────────────
export function Receitas() {
  const { condominioAtivo } = useAppStore()
  const [receitas, setReceitas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ data: hoje(), valor: '', descricao: '', categoria: 'OUTRO', referencia: '', periodo: '' })
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')

  const load = () => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get('/receitas', { params: { condominioId: condominioAtivo.id } })
      .then(r => setReceitas(r.data.receitas || [])).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [condominioAtivo])

  const openCreate = () => { setForm({ data: hoje(), valor: '', descricao: '', categoria: 'OUTRO', referencia: '', periodo: '' }); setEditId(null); setErro(''); setModal(true) }
  const openEdit = (r) => {
    setForm({ data: new Date(r.data).toISOString().split('T')[0], valor: r.valor, descricao: r.descricao, categoria: r.categoria, referencia: r.referencia || '', periodo: r.periodo || '' })
    setEditId(r.id); setErro(''); setModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      const payload = { ...form, condominioId: condominioAtivo.id, valor: parseFloat(form.valor) }
      if (editId) await api.put(`/receitas/${editId}`, payload)
      else await api.post('/receitas', payload)
      setModal(false); load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar receita?')) return
    await api.delete(`/receitas/${id}`); load()
  }

  return (
    <>
      <Topbar title="Receitas" actions={<button className="btn btn-sm btn-primary" onClick={openCreate}><Plus size={13} /> Nova receita</button>} />
      <div className="page-content">
        <div className="page-header"><h1>Receitas</h1><p>Total: {fmtEuro(receitas.reduce((s, r) => s + r.valor, 0))}</p></div>
        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Data</th><th>Descrição</th><th>Fração</th><th>Categoria</th><th>Período</th><th>Valor</th><th></th></tr></thead>
                <tbody>
                  {receitas.length === 0
                    ? <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem receitas.</td></tr>
                    : receitas.map(r => (
                      <tr key={r.id}>
                        <td>{fmtDate(r.data)}</td>
                        <td>{r.descricao}</td>
                        <td>{r.fracaoDesignacao ? <span className="badge badge-blue">{r.fracaoDesignacao}</span> : '—'}</td>
                        <td><span className={`badge badge-${r.categoria === 'QUOTA' ? 'green' : 'gray'}`}>{r.categoria}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.periodo || '—'}</td>
                        <td style={{ fontWeight: 600, color: 'var(--green-dark)' }}>{fmtEuro(r.valor)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm" onClick={() => openEdit(r)}><Pencil size={11} /></button>
                            <button className="btn btn-sm" style={{ color: 'var(--red-dark)' }} onClick={() => handleDelete(r.id)}><Trash2 size={11} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Editar receita' : 'Nova receita'}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Data *</label><input className="form-input" type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required /></div>
                <div className="form-group"><label className="form-label">Valor (€) *</label><input className="form-input" type="number" step="0.01" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} required /></div>
              </div>
              <div className="form-group"><label className="form-label">Descrição *</label><input className="form-input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} required /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Categoria</label>
                  <select className="form-select" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                    <option value="QUOTA">Quota</option><option value="FUNDO_RESERVA">Fundo Reserva</option><option value="JURO">Juro</option><option value="OUTRO">Outro</option>
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Período (ex: 2025-07)</label><input className="form-input" placeholder="AAAA-MM" value={form.periodo} onChange={e => setForm({ ...form, periodo: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Referência</label><input className="form-input" value={form.referencia} onChange={e => setForm({ ...form, referencia: e.target.value })} /></div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : editId ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

// ─── DOCUMENTOS ──────────────────────────────────────────────────────────────
const DOC_CATS = ['ATA', 'CONVOCATORIA', 'CONTRATO', 'ORCAMENTO', 'LEGAL', 'OUTRO']
const DOC_COLOR = { ATA: 'blue', CONVOCATORIA: 'blue', CONTRATO: 'green', ORCAMENTO: 'amber', LEGAL: 'red', OUTRO: 'gray' }

export function Documentos() {
  const { condominioAtivo } = useAppStore()
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('')
  const [modalUpload, setModalUpload] = useState(false)
  const [modalEdit, setModalEdit] = useState(null)
  const [form, setForm] = useState({ nome: '', categoria: 'OUTRO', descricao: '' })
  const [ficheiro, setFicheiro] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const fileRef = useRef()

  const load = () => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get('/documentos', { params: { condominioId: condominioAtivo.id, ...(filtro && { categoria: filtro }) } })
      .then(r => setDocs(r.data)).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [condominioAtivo, filtro])

  const handleUpload = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    if (!ficheiro) { setErro('Seleciona um ficheiro.'); setSaving(false); return }
    try {
      const fd = new FormData()
      fd.append('ficheiro', ficheiro)
      fd.append('nome', form.nome || ficheiro.name)
      fd.append('categoria', form.categoria)
      fd.append('descricao', form.descricao)
      fd.append('condominioId', condominioAtivo.id)
      await api.post('/documentos', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setModalUpload(false); setFicheiro(null); setForm({ nome: '', categoria: 'OUTRO', descricao: '' }); load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao carregar.') }
    finally { setSaving(false) }
  }

  const handleEdit = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      await api.put(`/documentos/${modalEdit.id}`, { nome: form.nome, categoria: form.categoria, descricao: form.descricao })
      setModalEdit(null); load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar documento? O ficheiro será removido.')) return
    await api.delete(`/documentos/${id}`); load()
  }

  const openEdit = (d) => {
    setForm({ nome: d.nome, categoria: d.categoria, descricao: d.descricao || '' })
    setErro(''); setModalEdit(d)
  }

  // URL correta: API_BASE + /uploads/ + path relativo guardado na BD
  const docUrl = (d) => `${API_BASE}/uploads/${d.path}`

  return (
    <>
      <Topbar title="Documentos" actions={
        <button className="btn btn-sm btn-primary" onClick={() => { setErro(''); setFicheiro(null); setForm({ nome: '', categoria: 'OUTRO', descricao: '' }); setModalUpload(true) }}>
          <Upload size={13} /> Carregar documento
        </button>
      } />
      <div className="page-content">
        <div className="page-header"><h1>Documentos</h1><p>Atas, contratos, convocatórias e documentos legais</p></div>
        <div className="tabs">
          <button className={`tab ${filtro === '' ? 'active' : ''}`} onClick={() => setFiltro('')}>Todos</button>
          {DOC_CATS.map(c => <button key={c} className={`tab ${filtro === c ? 'active' : ''}`} onClick={() => setFiltro(c)}>{c}</button>)}
        </div>
        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Nome</th><th>Categoria</th><th>Descrição</th><th>Data</th><th>Tamanho</th><th></th></tr></thead>
                <tbody>
                  {docs.length === 0
                    ? <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem documentos.</td></tr>
                    : docs.map(d => (
                      <tr key={d.id}>
                        <td style={{ fontWeight: 500 }}>{d.nome}</td>
                        <td><span className={`badge badge-${DOC_COLOR[d.categoria] || 'gray'}`}>{d.categoria}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.descricao || '—'}</td>
                        <td>{fmtDate(d.createdAt)}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.tamanho ? `${Math.round(d.tamanho / 1024)} KB` : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <a href={docUrl(d)} target="_blank" rel="noreferrer">
                              <button className="btn btn-sm" title="Abrir / Descarregar"><Download size={12} /></button>
                            </a>
                            <button className="btn btn-sm" onClick={() => openEdit(d)} title="Editar"><Pencil size={12} /></button>
                            <button className="btn btn-sm" style={{ color: 'var(--red-dark)' }} onClick={() => handleDelete(d.id)} title="Eliminar"><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal upload */}
      {modalUpload && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalUpload(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">Carregar documento</span><button onClick={() => setModalUpload(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button></div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label className="form-label">Ficheiro *</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input ref={fileRef} type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.jpg,.png,.xlsx,.xls" onChange={e => { setFicheiro(e.target.files[0]); if (!form.nome) setForm(f => ({ ...f, nome: e.target.files[0]?.name || '' })) }} />
                  <button type="button" className="btn" onClick={() => fileRef.current.click()}><Upload size={13} /> Selecionar</button>
                  {ficheiro && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{ficheiro.name}</span>}
                </div>
              </div>
              <div className="form-group"><label className="form-label">Nome</label><input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Categoria</label>
                <select className="form-select" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                  {DOC_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Descrição</label><input className="form-input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModalUpload(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Carregar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar */}
      {modalEdit && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalEdit(null)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">Editar documento</span><button onClick={() => setModalEdit(null)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button></div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleEdit}>
              <div className="form-group"><label className="form-label">Nome</label><input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Categoria</label>
                <select className="form-select" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                  {DOC_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Descrição</label><input className="form-input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModalEdit(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

// ─── RELATÓRIOS ──────────────────────────────────────────────────────────────
export function Relatorios() {
  const { condominioAtivo } = useAppStore()
  const [dividas, setDividas] = useState(null)
  const [balancete, setBalancete] = useState(null)
  const [tab, setTab] = useState('dividas')
  const anoAtual = new Date().getFullYear()

  useEffect(() => {
    if (!condominioAtivo) return
    api.get(`/relatorios/${condominioAtivo.id}/dividas`).then(r => setDividas(r.data)).catch(() => {})
    api.get(`/relatorios/${condominioAtivo.id}/balancete`, { params: { ano: anoAtual } }).then(r => setBalancete(r.data)).catch(() => {})
  }, [condominioAtivo])

  return (
    <>
      <Topbar title="Relatórios" />
      <div className="page-content">
        <div className="page-header"><h1>Relatórios</h1><p>Análise financeira do condomínio</p></div>
        <div className="tabs">
          <button className={`tab ${tab === 'dividas' ? 'active' : ''}`} onClick={() => setTab('dividas')}>Mapa de dívidas</button>
          <button className={`tab ${tab === 'balancete' ? 'active' : ''}`} onClick={() => setTab('balancete')}>Balancete {anoAtual}</button>
        </div>

        {tab === 'dividas' && (
          <div className="card">
            {!dividas ? <div className="loading-center"><span className="spinner" /></div> : (
              <>
                <div className="card-header">
                  <span className="card-title">Quotas em falta (desde {dividas.anoInicioQuotas || 2026})</span>
                  <span style={{ fontWeight: 700, color: 'var(--red-dark)' }}>Total: {fmtEuro(dividas.totalDivida)}</span>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Fração</th><th>Condómino</th><th>Quota/mês</th><th>Meses em falta</th><th>Total dívida</th><th>Estado</th></tr></thead>
                    <tbody>
                      {dividas.dividas?.length === 0
                        ? <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--green-dark)', padding: 24 }}>🎉 Todas as quotas em dia!</td></tr>
                        : dividas.dividas?.map(d => (
                          <tr key={d.fracao}>
                            <td><strong>{d.fracao}</strong></td>
                            <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.condominos?.join(', ') || '—'}</td>
                            <td>{fmtEuro(d.valorQuota)}</td>
                            <td style={{ fontWeight: d.mesesEmDivida > 0 ? 600 : 400, color: d.mesesEmDivida > 0 ? 'var(--red-dark)' : 'var(--text-secondary)' }}>
                              {d.mesesEmDivida > 0 ? `${d.mesesEmDivida} meses` : '—'}
                            </td>
                            <td style={{ fontWeight: 700, color: d.totalDivida > 0 ? 'var(--red-dark)' : 'var(--green-dark)' }}>{fmtEuro(d.totalDivida)}</td>
                            <td><span className={`badge badge-${d.estado === 'REGULARIZADO' ? 'green' : 'red'}`}>{d.estado === 'REGULARIZADO' ? 'Regularizado' : 'Em atraso'}</span></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'balancete' && (
          <div className="card">
            {!balancete ? <div className="loading-center"><span className="spinner" /></div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div style={{ padding: 14, background: 'var(--green-light)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--green-dark)' }}>Total receitas</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green-dark)' }}>{fmtEuro(balancete.totalReceitas)}</div>
                  </div>
                  <div style={{ padding: 14, background: 'var(--red-light)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red-dark)' }}>Total despesas</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--red-dark)' }}>{fmtEuro(balancete.totalDespesas)}</div>
                  </div>
                  <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Resultado</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: balancete.resultado >= 0 ? 'var(--green-dark)' : 'var(--red-dark)' }}>{fmtEuro(balancete.resultado)}</div>
                  </div>
                </div>
                {Object.entries(balancete.despesasPorCategoria || {}).map(([cat, val]) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{cat}</span>
                    <span style={{ fontWeight: 600 }}>{fmtEuro(val)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
